
/* ============================== src/hooks/useForm.js ============================== */
// src/hooks/useForm.js
import { useCallback, useMemo, useState } from "react";

const setByPath = (prev, path, value) => {
  const [head, ...rest] = String(path).split(".");
  if (rest.length === 0) return { ...prev, [head]: value };
  const nested =
    prev && typeof prev[head] === "object" && prev[head] !== null
      ? prev[head]
      : {};
  return { ...prev, [head]: setByPath(nested, rest.join("."), value) };
};

const getByPath = (obj, path) => {
  if (!obj) return undefined;
  let cur = obj;
  for (const key of String(path).split(".")) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = cur[key];
  }
  return cur;
};

export const useForm = (initialValues, validate, options) => {
  if (!options || !Array.isArray(options.fields)) {
    throw new Error(
      "useForm: опция fields обязательна - явный массив полей формы, " +
        "которые участвуют в проверке isDirty. Это защищает от «тихого» " +
        "сравнения служебных и побочных полей (файлы, логи, фотографии, " +
        "actualHours), которые сохраняются отдельно и не должны открывать " +
        "кнопку «Сохранить».",
    );
  }

  const { fields } = options;

  const [baseline] = useState(() =>
    typeof initialValues === "function" ? initialValues() : initialValues,
  );
  const [values, setValues] = useState(baseline);
  const [touched, setTouched] = useState({});

  const errors = useMemo(
    () => (validate ? validate(values) : {}),
    [values, validate],
  );

  const isValid = useMemo(() => Object.keys(errors).length === 0, [errors]);

  const isDirty = useMemo(() => {
    for (const key of fields) {
      if (
        JSON.stringify(getByPath(values, key)) !==
        JSON.stringify(getByPath(baseline, key))
      ) {
        return true;
      }
    }
    return false;
  }, [values, baseline, fields]);

  const handleChange = useCallback((field, value) => {
    setValues((prev) => setByPath(prev, field, value));
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  const setFieldValue = useCallback((field, value) => {
    setValues((prev) => setByPath(prev, field, value));
  }, []);

  const handleSubmit = (callback) => (e) => {
    e?.preventDefault();
    const allTouched = Object.keys(values).reduce(
      (acc, key) => ({ ...acc, [key]: true }),
      {},
    );
    setTouched(allTouched);
    if (isValid) callback(values);
  };

  const resetForm = () => {
    setValues(baseline);
    setTouched({});
  };

  return {
    values,
    setValues,
    handleChange,
    handleSubmit,
    errors,
    touched,
    setTouched,
    setFieldValue,
    resetForm,
    isValid,
    isDirty,
  };
};
